import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});


// Interceptor for JWT Auth Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Remove Content-Type header if sending FormData so browser automatically sets multipart/form-data with boundary
    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type');
          config.headers.delete('content-type');
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor for Error Handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn('Unauthorized token, redirecting to login...');
      localStorage.removeItem('admin_token');
    }
    return Promise.reject(error);
  }
);

// API Service Wrapper calling live Express backend endpoints
export const authService = {
  login: async (email, password, role) => {
    const res = await api.post('/auth/login', { email, password, role });
    return res.data;
  },
  validateUnique: async (fields) => {
    const res = await api.post('/auth/validate-unique', fields);
    return res.data;
  },
};

export const userService = {
  getUsers: async (params = {}) => {
    const res = await api.get('/users', { params });
    return { data: res.data, total: res.data.length };
  },
  getUserById: async (id) => {
    const res = await api.get(`/users/${id}`);
    return res.data;
  },
  createUser: async (userData) => {
    const res = await api.post('/users', userData);
    return res.data;
  },
  updateUserStatus: async (id, status) => {
    const res = await api.put(`/users/${id}/status`, { status });
    return res.data;
  },
  updateUser: async (id, userData) => {
    // console.log(id, userData)
    const res = await api.put(`/users/${id}`, userData);
    return res.data;
  },
  deleteUser: async (id) => {
    const res = await api.delete(`/users/${id}`);
    return res.data;
  },
  validateUnique: async (fields) => {
    const res = await api.post('/users/validate-unique', fields);
    return res.data;
  },
  trimUsers: async () => {
    const res = await api.post('/users/trim');
    return res.data;
  },
};

export const analyticsService = {
  getStats: async () => {
    const res = await api.get('/analytics');
    return res.data;
  },
  getCharts: async () => {
    const res = await api.get('/analytics/charts');
    return res.data;
  },
};

export const verificationService = {
  getRequests: async () => {
    const res = await api.get('/verification');
    return res.data;
  },
  updateStatus: async (id, statusOrData, optionalData = {}) => {
    const payload =
      typeof statusOrData === 'object'
        ? statusOrData
        : { status: statusOrData, ...optionalData };
    const res = await api.put(`/verification/${id}`, payload);
    return res.data;
  },
  createRequest: async (data) => {
    const res = await api.post('/verification', data);
    return res.data;
  },
  deleteRequest: async (id) => {
    const res = await api.delete(`/verification/${id}`);
    return res.data;
  },
};

export const matchService = {
  getMatches: async () => {
    const res = await api.get('/matches');
    return res.data;
  },
  createMatch: async (user1Id, user2Id) => {
    const res = await api.post('/matches', { user1Id, user2Id });
    return res.data;
  },
  deleteMatch: async (id) => {
    const res = await api.delete(`/matches/${id}`);
    return res.data;
  },
};

export const invitationService = {
  getInvitations: async () => {
    const res = await api.get('/invitations');
    return res.data;
  },
  createInvitation: async (data) => {
    const res = await api.post('/invitations', data);
    return res.data;
  },
  updateStatus: async (id, status) => {
    const res = await api.put(`/invitations/${id}/status`, { status });
    return res.data;
  },
  updateInvitation: async (id, data) => {
    const res = await api.put(`/invitations/${id}`, data);
    return res.data;
  },
  deleteInvitation: async (id) => {
    const res = await api.delete(`/invitations/${id}`);
    return res.data;
  },
};

export const eventService = {
  getEvents: async () => {
    const res = await api.get('/events');
    return res.data;
  },
  createEvent: async (eventData) => {
    const res = await api.post('/events', eventData);
    return res.data;
  },
  updateEvent: async (id, eventData) => {
    const res = await api.put(`/events/${id}`, eventData);
    return res.data;
  },
  approveVenue: async (id) => {
    const res = await api.put(`/events/${id}/approve`);
    return res.data;
  },
  deleteEvent: async (id) => {
    const res = await api.delete(`/events/${id}`);
    return res.data;
  },
};

export const participantService = {
  getParticipants: async (params = {}) => {
    const res = await api.get('/participants', { params });
    return res.data;
  },
  getParticipantById: async (id) => {
    const res = await api.get(`/participants/${id}`);
    return res.data;
  },
  createParticipant: async (data) => {
    const res = await api.post('/participants', data);
    return res.data;
  },
  updateAttendance: async (id, attendanceData) => {
    const res = await api.put(`/participants/${id}/attendance`, attendanceData);
    return res.data;
  },
  refundParticipant: async (id) => {
    const res = await api.post(`/participants/${id}/refund`);
    return res.data;
  },
  deleteParticipant: async (id) => {
    const res = await api.delete(`/participants/${id}`);
    return res.data;
  },
};

export const paymentService = {
  getTransactions: async (params = {}) => {
    const res = await api.get('/payments', { params });
    return res.data;
  },
  getTransactionStats: async () => {
    const res = await api.get('/payments/stats');
    return res.data;
  },
  getTransactionById: async (id) => {
    const res = await api.get(`/payments/${id}`);
    return res.data;
  },
  createTransaction: async (data) => {
    const res = await api.post('/payments', data);
    return res.data;
  },
  refundTransaction: async (id, data = {}) => {
    const res = await api.post(`/payments/${id}/refund`, data);
    return res.data;
  },
  clearTransactions: async () => {
    const res = await api.delete('/payments');
    return res.data;
  },
};

export const supportService = {
  getTickets: async () => {
    const res = await api.get('/support-tickets');
    return res.data;
  },
  updateStatus: async (id, status) => {
    const res = await api.put(`/support-tickets/${id}/status`, { status });
    return res.data;
  },
};

export const moderationService = {
  getReports: async () => {
    const res = await api.get('/moderation-reports');
    return res.data;
  },
  takeAction: async (id, actionType) => {
    const res = await api.post(`/moderation-reports/${id}/action`, { actionType });
    return res.data;
  },
};

export const logService = {
  getLogs: async (params = {}) => {
    const res = await api.get('/logs', { params });
    return res.data;
  },
  getUserLogs: async (userId) => {
    const res = await api.get(`/logs/user/${userId}`);
    return res.data;
  },
  generateUserLogs: async () => {
    const res = await api.post('/logs/generate');
    return res.data;
  },
  createLog: async (logData) => {
    const res = await api.post('/logs', logData);
    return res.data;
  },
  clearLogs: async () => {
    const res = await api.delete('/logs');
    return res.data;
  },
};

export const settingsService = {
  getSettings: async () => {
    const res = await api.get('/settings');
    return res.data;
  },
  updateSettings: async (settings) => {
    const res = await api.put('/settings', settings);
    return res.data;
  },
};

export const adminService = {
  getAdmins: async () => {
    const res = await api.get('/admins');
    return res.data;
  },
  createAdmin: async (data) => {
    const res = await api.post('/admins', data);
    return res.data;
  },
  updateAdmin: async (id, data) => {
    const res = await api.put(`/admins/${id}`, data);
    return res.data;
  },
  toggleStatus: async (id, status) => {
    const res = await api.patch(`/admins/${id}/status`, { status });
    return res.data;
  },
  deleteAdmin: async (id) => {
    const res = await api.delete(`/admins/${id}`);
    return res.data;
  },
};

export const roleService = {
  getMatrix: async () => {
    const res = await api.get('/roles');
    return res.data;
  },
  updateMatrix: async (matrix) => {
    const res = await api.put('/roles', matrix);
    return res.data;
  },
  getRoles: async () => {
    const res = await api.get('/roles/list');
    return res.data;
  },
  createRole: async (roleData) => {
    const res = await api.post('/roles', roleData);
    return res.data;
  },
  updateRole: async (id, roleData) => {
    const res = await api.put(`/roles/${id}`, roleData);
    return res.data;
  },
  deleteRole: async (id) => {
    const res = await api.delete(`/roles/${id}`);
    return res.data;
  },
};

export const cmsService = {
  getContent: async () => {
    const res = await api.get('/cms');
    return res.data;
  },
  updateContent: async (content) => {
    const res = await api.put('/cms', content);
    return res.data;
  },
  addFAQ: async (faqData) => {
    const res = await api.post('/cms/faqs', faqData);
    return res.data;
  },
  updateFAQ: async (id, faqData) => {
    const res = await api.put(`/cms/faqs/${id}`, faqData);
    return res.data;
  },
  deleteFAQ: async (id) => {
    const res = await api.delete(`/cms/faqs/${id}`);
    return res.data;
  },
  addBanner: async (bannerData) => {
    const res = await api.post('/cms/banners', bannerData);
    return res.data;
  },
  updateBanner: async (id, bannerData) => {
    const res = await api.put(`/cms/banners/${id}`, bannerData);
    return res.data;
  },
  deleteBanner: async (id) => {
    const res = await api.delete(`/cms/banners/${id}`);
    return res.data;
  },
};

export const campaignService = {
  getCampaigns: async () => {
    const res = await api.get('/campaigns');
    return res.data;
  },
  sendCampaign: async (campaignData) => {
    const res = await api.post('/campaigns', campaignData);
    return res.data;
  },
};

export const notificationService = {
  getNotifications: async () => {
    const res = await api.get('/notifications');
    return res.data;
  },
  getStats: async () => {
    const res = await api.get('/notifications/stats');
    return res.data;
  },
  broadcast: async (data) => {
    const res = await api.post('/notifications/broadcast', data);
    return res.data;
  },
  deleteNotification: async (id) => {
    const res = await api.delete(`/notifications/${id}`);
    return res.data;
  },
  markAllRead: async () => {
    const res = await api.put('/notifications/read-all');
    return res.data;
  },
};

export const moderatorService = {
  getModerators: async () => {
    const res = await api.get('/moderators');
    return res.data;
  },
  addModerator: async (data) => {
    const res = await api.post('/moderators', data);
    return res.data;
  },
};

export const partnerService = {
  getPartners: async () => {
    const res = await api.get('/partners');
    return res.data;
  },
  addPartner: async (data) => {
    const res = await api.post('/partners', data);
    return res.data;
  },
};

export const subscriptionService = {
  getPlans: async () => {
    const res = await api.get('/subscriptions/plans');
    return res.data;
  },
  createPlan: async (planData) => {
    const res = await api.post('/subscriptions/plans', planData);
    return res.data;
  },
  updatePlan: async (id, planData) => {
    const res = await api.put(`/subscriptions/plans/${id}`, planData);
    return res.data;
  },
  deletePlan: async (id) => {
    const res = await api.delete(`/subscriptions/plans/${id}`);
    return res.data;
  },
  createPromo: async (promoData) => {
    const res = await api.post('/subscriptions/promos', promoData);
    return res.data;
  },
};

export const uploadService = {
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await api.post('/upload', formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return res.data;
  },
  uploadImages: async (files) => {
    const formData = new FormData();
    const fileList = Array.from(files);
    for (const file of fileList) {
      formData.append('images', file);
    }
    const res = await api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return res.data;
  },
};

export const categoryService = {
  getCategories: async () => {
    const res = await api.get('/categories');
    return res.data;
  },
  createCategory: async (data) => {
    const res = await api.post('/categories', data);
    return res.data;
  },
  updateCategory: async (id, data) => {
    const res = await api.put(`/categories/${id}`, data);
    return res.data;
  },
  deleteCategory: async (id) => {
    const res = await api.delete(`/categories/${id}`);
    return res.data;
  },
  addSubCategory: async (id, subData) => {
    const res = await api.post(`/categories/${id}/subcategories`, subData);
    return res.data;
  },
  updateSubCategory: async (id, subId, subData) => {
    const res = await api.put(`/categories/${id}/subcategories/${subId}`, subData);
    return res.data;
  },
  deleteSubCategory: async (id, subId) => {
    const res = await api.delete(`/categories/${id}/subcategories/${subId}`);
    return res.data;
  },
};

export const venueService = {
  getVenues: async (params = {}) => {
    const res = await api.get('/venues', { params });
    return res.data;
  },
  getVenueById: async (id) => {
    const res = await api.get(`/venues/${id}`);
    return res.data;
  },
  createVenue: async (venueData) => {
    const res = await api.post('/venues', venueData);
    return res.data;
  },
  updateVenue: async (id, venueData) => {
    const res = await api.put(`/venues/${id}`, venueData);
    return res.data;
  },
  updateVenueStatus: async (id, status) => {
    const res = await api.put(`/venues/${id}/status`, { status });
    return res.data;
  },
  deleteVenue: async (id) => {
    const res = await api.delete(`/venues/${id}`);
    return res.data;
  },
  getVenueCategories: async () => {
    const res = await api.get('/venues/categories');
    return res.data;
  },
  createVenueCategory: async (categoryData) => {
    const res = await api.post('/venues/categories', categoryData);
    return res.data;
  },
  updateVenueCategory: async (id, categoryData) => {
    const res = await api.put(`/venues/categories/${id}`, categoryData);
    return res.data;
  },
  deleteVenueCategory: async (id) => {
    const res = await api.delete(`/venues/categories/${id}`);
    return res.data;
  },
};

export const offerService = {
  getOffers: async (params = {}) => {
    const res = await api.get('/offers', { params });
    return res.data;
  },
  getOfferById: async (id) => {
    const res = await api.get(`/offers/${id}`);
    return res.data;
  },
  createOffer: async (data) => {
    const res = await api.post('/offers', data);
    return res.data;
  },
  updateOffer: async (id, data) => {
    const res = await api.put(`/offers/${id}`, data);
    return res.data;
  },
  deleteOffer: async (id) => {
    const res = await api.delete(`/offers/${id}`);
    return res.data;
  },
};

export default api;
